/**
 * Uzak görsel (kafa sprite'ı) — **yüklenemezse kırık ikon gösterme**.
 *
 * Yerelde bile görüldü: worker aynı anda birkaç karakter yazarken kartlardaki görseller
 * düşebiliyor ve tarayıcı kırık görsel ikonu çiziyor. Etkinlikte bu misafirin gördüğü şey olur.
 * Bu yüzden birkaç kez yeniden denenir (artan bekleme, cache atlatan parametre), olmazsa
 * `onGiveUp` ile çizili yedeğe düşülür.
 */

import { useEffect, useState } from 'react'

const RETRY_DELAYS_MS = [400, 1200, 3000]

export function useRetryingImage(url: string | null): {
  src: string | null
  failed: boolean
  /** Yüklenen sprite'ın en/boy oranı; yüklenene kadar `null`. Kafa sprite'ı **kare değil**
   *  (HEAD_STANDARD_V1: 1254x1504) ve tuval boyu profille değişebiliyor (#70) — oranı
   *  görselin kendisinden okumak, kioskta ikinci bir kopya sabit tutmaktan iyidir. */
  aspect: number | null
} {
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  const [seen, setSeen] = useState(url)
  const [aspect, setAspect] = useState<number | null>(null)

  // URL değişince sayaç sıfırlanır — render sırasında, effect'te değil (React'in önerdiği kalıp)
  if (seen !== url) {
    setSeen(url)
    setAttempt(0)
    setFailed(false)
    setAspect(null)
  }

  useEffect(() => {
    if (!url) return
    let cancelled = false
    const image = new Image()
    image.onload = () => {
      if (!cancelled && image.naturalHeight > 0) {
        setAspect(image.naturalWidth / image.naturalHeight)
      }
    }
    image.onerror = () => {
      if (cancelled) return
      const delay = RETRY_DELAYS_MS[attempt]
      if (delay === undefined) {
        setFailed(true)
        return
      }
      setTimeout(() => {
        if (!cancelled) setAttempt((a) => a + 1)
      }, delay)
    }
    image.src = attempt === 0 ? url : `${url}${url.includes('?') ? '&' : '?'}r=${attempt}`
    return () => {
      cancelled = true
      image.onload = null
      image.onerror = null
    }
  }, [url, attempt])

  if (!url || failed) return { src: null, failed, aspect: null }
  return {
    src: attempt === 0 ? url : `${url}${url.includes('?') ? '&' : '?'}r=${attempt}`,
    failed,
    aspect,
  }
}
